import { useEffect, useState } from 'react';
import { Grid, Typography, makeStyles } from '@material-ui/core';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import BarChartIcon from '@material-ui/icons/BarChart';
import ListAltIcon from '@material-ui/icons/ListAlt';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import {
  Content,
  Header,
  InfoCard,
  Link,
  Page,
  Progress,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  HomePageStarredEntities,
  HomePageToolkit,
} from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { PlatformHealthCard } from '../health/PlatformHealthCard';
import { CatalogAuditCard } from '../health/CatalogAuditCard';

// Интерактивная схема всей платформы (components/architecture): системы, компоненты, API и ресурсы.
export const ARCHITECTURE_PATH = '/architecture';

const useStyles = makeStyles(theme => ({
  searchBar: {
    display: 'flex',
    maxWidth: '60vw',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    padding: '8px 0',
    borderRadius: '50px',
    margin: 'auto',
    // Поле поиска внутри скруглённой обёртки: убираем его собственную прямоугольную рамку.
    '& fieldset': { border: 'none' },
  },
  systemTitle: {
    fontWeight: 600,
  },
}));

const SystemsCard = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [systems, setSystems] = useState<Entity[]>();
  const [error, setError] = useState<Error>();

  useEffect(() => {
    catalogApi
      .getEntities({
        filter: { kind: 'system' },
        fields: ['metadata.name', 'metadata.title', 'metadata.description'],
      })
      .then(response =>
        setSystems(
          [...response.items].sort((a, b) =>
            (a.metadata.title ?? a.metadata.name).localeCompare(
              b.metadata.title ?? b.metadata.name,
            ),
          ),
        ),
      )
      .catch(setError);
  }, [catalogApi]);

  return (
    <InfoCard
      title="Системы"
      deepLink={{ title: 'Общий граф архитектуры', link: ARCHITECTURE_PATH }}
    >
      {error && <Typography color="error">{error.message}</Typography>}
      {!systems && !error && <Progress />}
      <Grid container spacing={2}>
        {systems?.map(system => {
          const base = `/catalog/default/system/${system.metadata.name}`;
          return (
            <Grid item xs={12} md={6} key={system.metadata.name}>
              <Link to={base} className={classes.systemTitle}>
                {system.metadata.title ?? system.metadata.name}
              </Link>
              {' · '}
              <Link to={`${base}/diagram`}>диаграмма</Link>
              <Typography variant="body2" color="textSecondary">
                {system.metadata.description}
              </Typography>
            </Grid>
          );
        })}
      </Grid>
    </InfoCard>
  );
};

const tools = [
  {
    url: ARCHITECTURE_PATH,
    label: 'Архитектура',
    icon: <AccountTreeIcon />,
  },
  {
    url: 'https://monitoring.livedigital.space',
    label: 'Grafana',
    icon: <BarChartIcon />,
  },
  {
    url: 'https://glog.livedigital.space',
    label: 'Graylog',
    icon: <ListAltIcon />,
  },
  {
    url: 'https://github.com/VLprojects/kubeconfig',
    label: 'kubeconfig',
    icon: <CloudQueueIcon />,
  },
  {
    url: 'https://github.com/VLprojects/backstage-catalog/blob/master/docs/conventions.md',
    label: 'Конвенции каталога',
    icon: <MenuBookIcon />,
  },
];

export const HomePage = () => {
  const classes = useStyles();

  return (
    <Page themeId="home">
      <Header
        title="LiveDigital Backstage"
        subtitle="Архитектура платформы livedigital"
      />
      <Content>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <HomePageSearchBar
              className={classes.searchBar}
              placeholder="Поиск по порталу"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <PlatformHealthCard />
          </Grid>
          <Grid item xs={12} md={4}>
            <HomePageToolkit title="Быстрые ссылки" tools={tools} />
          </Grid>
          <Grid item xs={12} md={8}>
            <SystemsCard />
          </Grid>
          <Grid item xs={12} md={4}>
            <HomePageStarredEntities title="Избранное" />
          </Grid>
          <Grid item xs={12} md={8}>
            <CatalogAuditCard />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
